/**
 * Shipyard build list, rendered into the in-play "Build" window (an .ar-owin panel
 * managed by game.ts — the same window chrome as the Fleet window). Only 200-point
 * cities the side owns are shipyards. Building is an INSTANT purchase spent from the
 * single points pool (the same points cities pay you and that decide the winner).
 * Every order goes through the pure purchaseShip (rules/turn.ts), the single source
 * of truth shared with the AI.
 */
import { UNITS } from '../rules/ships';
import { purchaseShip } from '../rules/turn';
import { ICON_VP } from './icons';
import type { City, GameState, ShipType, Side } from '../rules/state';

const ORDER: ShipType[] = ['CV', 'BB', 'CR', 'SS', 'DD', 'PT', 'AT', 'TR', 'FT'];

// Yard cards + build buttons, scoped inside the .ar-owin Build window body.
const CSS = `
.ar-owin .yard { background: linear-gradient(180deg, hsla(0, 0%, 100%, 0.5), hsla(0, 0%, 100%, 0.25));
  border: 1px solid var(--wood); border-radius: 4px; padding: 7px 8px; margin-bottom: 8px;
  box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.4); }
.ar-owin .yard b { color: var(--head); font-size: 13px; }
.ar-owin .yard .builds { display: grid; grid-template-columns: 1fr; gap: 4px; margin-top: 6px; }
.ar-owin .yard button { display: flex; align-items: baseline; justify-content: space-between; gap: 8px;
  font-family: inherit; font-weight: 700; cursor: pointer; padding: 5px 8px; color: var(--txt);
  border: 1px solid var(--wood-d); border-radius: 3px;
  background: linear-gradient(180deg, var(--brass-l), var(--brass) 60%, var(--brass-d));
  box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.5); font-size: 12px; line-height: 1.2; text-align: left; }
.ar-owin .yard button:hover:not(:disabled) { filter: brightness(1.06); }
.ar-owin .yard button:active:not(:disabled) { box-shadow: inset 0 2px 3px hsla(0, 0%, 0%, 0.3); }
.ar-owin .yard button .ac { color: var(--wood-d); font-weight: 400; }
.ar-owin .yard button .pts { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 2px; font-size: 11px; color: var(--wood-d); font-weight: 400; white-space: nowrap; }
.ar-owin .yard button .pts svg { width: 1.15em; height: 1.15em; display: block; }
.ar-owin .yard button:disabled { opacity: 0.4; cursor: default; }
`;

/**
 * Fill an .ar-owin window body with the side's shipyards and their build buttons.
 * Re-renders itself after each purchase so the points readout and affordability
 * update in place. `onBuilt` logs the order + refreshes the HUD/board.
 */
export function renderBuildYards(
  body: HTMLElement,
  g: GameState,
  side: Side,
  onBuilt: (msg: string) => void,
): void {
  if (!document.getElementById('ar-build-style')) {
    const style = document.createElement('style');
    style.id = 'ar-build-style';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  const yards = g.cities.filter((c) => c.owner === side && c.vp === 200);
  const points = g.vp[side];
  const yardsHtml = yards.length
    ? yards
        .map((y, yi) => {
          const buttons = `<div class="builds">${ORDER.map((t) => {
            const cost = UNITS[t].cost;
            const ok = points >= cost;
            return `<button data-yard="${yi}" data-type="${t}" ${ok ? '' : 'disabled'} title="${UNITS[t].name} — ${cost} points"><span class="nm">${UNITS[t].name} <span class="ac">(${t})</span></span><span class="pts">${ICON_VP}${cost}</span></button>`;
          }).join('')}</div>`;
          return `<div class="yard"><b>${y.name}</b>${buttons}</div>`;
        })
        .join('')
    : '<div class="none">No 200-point shipyard yet — capture one with a transport, Admiral.</div>';

  body.innerHTML = `<p class="hint">Buy ships instantly at a shipyard (a 200-point city you own). Spends from your treasury — the pool that decides the battle. You have <b>${ICON_VP}${points}</b>.</p>${yardsHtml}`;

  const yards2 = yards;
  body.querySelectorAll<HTMLButtonElement>('button[data-yard]').forEach((b) => {
    b.addEventListener('click', () => {
      const y: City = yards2[Number(b.dataset['yard'])]!;
      const t = b.dataset['type'] as ShipType;
      if (purchaseShip(g, y, t, side)) {
        onBuilt(`Commissioned a ${t} at ${y.name} (−${UNITS[t].cost} pts).`);
        renderBuildYards(body, g, side, onBuilt); // reflect spent points + the new ship
      }
    });
  });
}
